<template>
  <div>
    <b-dropdown right
                variant="link"
                no-caret
                class="d-inline-block align-middle">
      <template #button-content>
        <a class="btn btn-themed gray">
          <FileSvg class="mr-2 file-svg"/>{{ $t('questionnaire') }}
          <span v-if="alreadySigned" class="ml-2"><TickSvg class="svg-light"/></span>
        </a>
      </template>
      <b-dropdown-item>
        {{ $t('status') }}:
        <span v-if="['filled_not_signed', 'not_filled'].includes(status)" class="text-danger">{{ $t(status) }}</span>
        <span v-if="status === 'signed'" class="text-success">{{ $t('signed') }}{{ signedAt ? ' ('+formatDateTime(signedAt)+')' : ''}}</span>
      </b-dropdown-item>
      <b-dropdown-divider/>
      <b-dropdown-item v-if="notFilled" @click="sendLink">
        {{ $t('send_link_to_patient') }}
      </b-dropdown-item>
      <b-dropdown-item @click="fill">
        {{ $t('edit_questionnaire') }}
      </b-dropdown-item>
      <b-dropdown-item @click="print">
        {{ $t('print') }}
      </b-dropdown-item>
      <b-dropdown-divider/>
      <b-dropdown-item v-if="FDocEnabled && !alreadySigned && !anketaDocument"
                       @click="signViaFDoc">
        {{ $t('sign_via_vendor', { vendor: 'F.Doc' }) }}
      </b-dropdown-item>
      <b-dropdown-item @click="upload"
                       v-if="!anketaDocument && !generatedAnketaDocument">
        {{ $t('upload') }}
      </b-dropdown-item>
      <b-dropdown-item @click="viewDocument"
                       v-if="anketaDocument || generatedAnketaDocument">
        {{ $t('view') }}
      </b-dropdown-item>
      <b-dropdown-item @click="resetAnketaFilling"
                       v-if="anketaPatientFilled">
        {{ $t('reset_questionnaire_fill') }}
      </b-dropdown-item>
      <b-dropdown-item @click="deleteAnketaDocument"
                       v-if="anketaDocument">
        {{ $t('delete_attachment') }}
      </b-dropdown-item>
      <b-dropdown-item @click="deleteGeneratedAnketaDocument"
                       v-if="generatedAnketaDocument">
        {{ $t('delete_signed') }}
      </b-dropdown-item>
    </b-dropdown>
    <FileGroup filetype="pdf"
               :sizelimit="5"
               ref="childFileComponent"
               @input="anketaUploaded"
               style="display: none"/>
    <PatientAnketaLinkChannelsModal/>
  </div>
</template>

<script setup>
import FileSvg from "@/assets/svg-vue/patients/file.svg.vue"
import TickSvg from "@/assets/svg-vue/form/tick.svg.vue"
import { computed, getCurrentInstance, onBeforeMount, ref } from "vue"
import JwtService from "@/services/jwt.service"
import { useStore } from "vuex"
import FileGroup from "@/components/form/FileGroup.vue"
import { EloquentService } from "@/services/api/eloquent.api.service"
import { DocumentsService } from "@/services/api/documents.api.service"
import { PatientService } from "@/services/api/patient.api.service"
import PatientAnketaLinkChannelsModal from "@/components/parts/patients/PatientAnketaLinkChannelsModal.vue"
import {formatDateTime} from "@/extensions/filters/filters"

const instance = getCurrentInstance()

let bvModal = null
let i18n = null
let noty = null

const store = useStore()
const childFileComponent = ref(null)

const patient = computed(() => store.state.patient.patient)
const clinic = computed(() => store.state.auth.clinic)
const anketaDocument = computed(() => patient.value?.anketaDocument || null)
const generatedAnketaDocument = computed(() => patient.value?.generatedAnketaDocument || null)
const anketaPatientFilled = computed(() => patient.value?.anketa_patient_filled || null)
const signedAt = computed(() => (generatedAnketaDocument.value?.signedAtTimezone || anketaDocument.value?.uploadedTimezone || null))
const alreadySigned = computed(() => generatedAnketaDocument.value?.signed_at != null)
const FDocEnabled = computed(() => store.getters.FDocEnabled)

const notFilled = computed(() => !generatedAnketaDocument.value && !anketaPatientFilled.value?.submitted_at)
const status = computed(() => {
  if (alreadySigned.value) return 'signed'
  if (anketaDocument.value) return 'signed'
  if (anketaPatientFilled.value?.submitted_at) return 'filled_not_signed'
  return 'not_filled'
})

onBeforeMount(() => {
  bvModal = instance?.ctx?._bv__modal
  i18n = instance?.ctx?.$i18n
  noty = instance?.appContext?.app?.noty
})

const fill = () => {
  bvModal.show('anketa-modal')
}

const print = () => {
  let link = JwtService.getPrintPrelink(clinic.value)
  window.open(link+'/anketa/'+patient.value.id, '_blank')
}

const upload = () => {
  childFileComponent.value.triggerFileInput()
}

const anketaUploaded = async (file) => {
  try {
    const formData = new FormData()
    formData.append('original', file)
    formData.append('client_id', patient.value.id)
    formData.append('description', i18n.t('questionnaire'))
    formData.append('document_type', 'anketa')
    const res = await EloquentService.create('client_document', formData)
    if(res.data?.id) {
      noty.info(i18n.t('success_message'))
      store.commit('incrementDataTableCounter')
    }
  } catch (e) {
    console.log(e)
  }
}

const viewDocument = async () => {
  if(alreadySigned.value) {
    try {
      const res = await DocumentsService.loadFDoc(generatedAnketaDocument.value.id)
      if(res.data) {
        window.open(res.data, '_blank')
      }
    } catch (e) {
      console.log(e)
    }
    return
  }
  if(!anketaDocument.value) return
  window.open(anketaDocument.value.fileUrl, '_blank')
}

const deleteAnketaDocument = async () => {
  if(!anketaDocument.value) return
  let formData = new FormData()
  formData.append('description', anketaDocument.value.description)
  formData.append('document_type', 'custom')
  try {
    await EloquentService.update('client_document', anketaDocument.value.id, formData)
    store.commit('incrementDataTableCounter')
  } catch (e) {
    console.log(e)
  }
}

const deleteGeneratedAnketaDocument = async () => {
  if(!generatedAnketaDocument.value) return
  try {
    await EloquentService.delete('generated_document', generatedAnketaDocument.value.id)
    store.commit('incrementDataTableCounter')
  } catch (e) {
    console.log(e)
  }
}

const resetAnketaFilling = async () => {
  try {
    await PatientService.resetAnketaFilling(patient.value.id)
    store.commit('incrementDataTableCounter')
  } catch (e) {
    console.log(e)
  }
}

const signViaFDoc = async () => {
  try {
    const res = await PatientService.signClientAnketa(patient.value.id)
    if(res?.data) {
      window.open(res.data, '_blank')
    }
  } catch (e) {
    console.log(e)
  }
}

const sendLink = () => {
  bvModal.show('questionnaire-link-modal')
}
</script>
